<script setup lang="js">
import axios from "axios";
import OrdersStatus from "@/views/dashboards/components/OrdersStatus.vue";
import Remains from "@/views/dashboards/components/Remains.vue";
import ClientRating from "@/views/dashboards/components/ClientRating.vue";
import SalesOverview from "@/views/dashboards/components/SalesOverview.vue";
import OrderKindsOverview from "@/views/dashboards/components/OrderKindsOverview.vue";
import OrderFulfillmentOverview from "@/views/dashboards/components/OrderFulfillmentOverview.vue";
import { computed, defineProps, getCurrentInstance, onMounted, ref, watch } from "vue";

const props = defineProps({
    from: {
        type: String,
        required: false,
        default: null
    },
    to: {
        type: String,
        required: false,
        default: null
    }
});

const refresh = ref(false);
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const defaultStart = () => formatDate(new Date(new Date().getFullYear(), 0, 1));
const defaultEnd = () => formatDate(new Date());
const normalizeDateParam = (value, fallback) => {
    if (!value || value === "null" || value === "undefined") {
        return fallback();
    }

    return value;
};

const start = ref(normalizeDateParam(props.from, defaultStart));
const end = ref(normalizeDateParam(props.to, defaultEnd));
const successResult = ref(null);
const successLoading = ref(false);
const successError = ref(false);
const { proxy } = getCurrentInstance();
const t = (key) => proxy.$filters.localize(key);

const getTodayRange = () => {
    const today = new Date();
    return { from: formatDate(today), to: formatDate(today) };
};

const getWeekRange = () => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 6);
    return { from: formatDate(startDate), to: formatDate(today) };
};

const getMonthRange = () => {
    const today = new Date();
    return {
        from: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: formatDate(today)
    };
};
const getYearRange = () => {
    const today = new Date();
    return {
        from: formatDate(new Date(today.getFullYear(), 0, 1)),
        to: formatDate(today)
    };
};

const rangeButtons = computed(() => [
    { text: t("Dashboard.range.today"), query: getTodayRange() },
    { text: t("Dashboard.range.week"), query: getWeekRange() },
    { text: t("Dashboard.range.month"), query: getMonthRange() },
    { text: t("Dashboard.range.year"), query: getYearRange() }
]);

const loadSuccessDashboard = async () => {
    try {
        successResult.value = null;
        successError.value = false;
        successLoading.value = true;
        const response = await axios.get(`${window.location.origin}/ui/v3/dashboard/success`, {
            params: {
                start: normalizeDateParam(start.value, defaultStart),
                end: normalizeDateParam(end.value, defaultEnd)
            }
        });
        successResult.value = response.data;
    } catch {
        successError.value = true;
    } finally {
        successLoading.value = false;
    }
};

watch(
    () => props.from,
    (newValue) => {
        if (!newValue) {
            return;
        }

        refresh.value = !refresh.value;
        start.value = normalizeDateParam(newValue, defaultStart);
    }
);

watch(
    () => props.to,
    (newValue) => {
        refresh.value = !refresh.value;
        end.value = normalizeDateParam(newValue, defaultEnd);
    }
);

watch(start, loadSuccessDashboard);
watch(end, loadSuccessDashboard);
onMounted(loadSuccessDashboard);
</script>

<template>
    <div class="dashboard-page">
        <section class="dashboard-hero">
            <div class="dashboard-hero__content">
                <div class="dashboard-hero__eyebrow text-center">{{
                    $filters.localize("Dashboard.hero.eyebrow") }}</div>
                <h1 class="dashboard-hero__title text-center">{{
                    $filters.localize("Dashboard.hero.title") }}</h1>
                <p class="dashboard-hero__text text-center">
                    {{ $filters.localize("Dashboard.hero.text") }}
                </p>
            </div>

            <div class="dashboard-hero__controls">
                <div class="dashboard-range-buttons">
                    <router-link
                        v-for="(label, idx) in rangeButtons"
                        :key="idx"
                        :to="{ name: 'DashboardPage', query: label.query }"
                        class="dashboard-range-button"
                    >
                        {{ label.text }}
                    </router-link>
                </div>

                <div class="dashboard-date-card">
                    <label class="dashboard-date-card__label">{{
                        $filters.localize("Dashboard.period") }}</label>
                    <div class="dashboard-date-card__fields">
                        <input
                            type="date"
                            class="form-control"
                            v-model="start"
                        />
                        <span class="dashboard-date-card__separator">-</span>
                        <input
                            type="date"
                            class="form-control"
                            v-model="end"
                        />
                    </div>
                </div>
            </div>
        </section>

        <div class="dashboard-scroll-container">
            <section class="dashboard-overview-section">
                <sales-overview
                    :result-data="successResult"
                    :loading="successLoading"
                    :error="successError"
                />
            </section>

            <section class="dashboard-grid">
                <orders-status
                    :title="$filters.localize('Dashboard.status.inProgress')"
                    method="in-progress"
                    tone="info"
                    :start="start"
                    :end="end"
                />

                <orders-status
                    :title="$filters.localize('Dashboard.status.success')"
                    method="success"
                    tone="success"
                    :result-data="successResult"
                    :loading="successLoading"
                    :error="successError"
                />

                <orders-status
                    :title="$filters.localize('Dashboard.status.failed')"
                    method="failed"
                    tone="danger"
                    :start="start"
                    :end="end"
                />

                <remains :refresh="refresh" />
            </section>

            <section class="dashboard-overview-section">
                <order-kinds-overview
                    :start="start"
                    :end="end"
                    :result-data="successResult"
                    :loading="successLoading"
                    :error="successError"
                />
            </section>

            <section class="dashboard-overview-section">
                <order-fulfillment-overview
                    :start="start"
                    :end="end"
                    :result-data="successResult"
                    :loading="successLoading"
                    :error="successError"
                />
            </section>

            <section class="dashboard-secondary-grid">
                <client-rating
                    :start="start"
                    :end="end"
                />
            </section>
        </div>
    </div>
</template>

<style scoped>
.dashboard-page {
    --dashboard-ink: #17324d;
    --dashboard-muted: #6f8194;
    --dashboard-border: rgba(23, 50, 77, 0.08);
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100vh;
    overflow: hidden;
    padding: 1.5rem;
    background: #f7fafc;
}

.dashboard-hero {
    position: sticky;
    top: 0;
    z-index: 10;
    flex: 0 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(360px, 1fr);
    gap: 1rem;
    align-items: center;
    padding: 1.1rem 1.25rem;
    border: 1px solid var(--dashboard-border);
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 24px 80px rgba(31, 59, 87, 0.08);
}

.dashboard-hero__eyebrow {
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #bb6c31;
}

.dashboard-hero__title {
    margin: 0 0 0.45rem;
    color: var(--dashboard-ink);
    font-size: clamp(1.35rem, 2vw, 2rem);
    line-height: 1.15;
}

.dashboard-hero__text {
    max-width: 48rem;
    margin: 0 auto;
    color: var(--dashboard-muted);
    font-size: 0.9rem;
    line-height: 1.45;
    text-align: center;
}

.dashboard-hero__controls {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.dashboard-range-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.dashboard-range-button {
    padding: 0.6rem 0.9rem;
    border: 1px solid rgba(23, 50, 77, 0.12);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.78);
    color: var(--dashboard-ink);
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.dashboard-range-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 30px rgba(23, 50, 77, 0.08);
    border-color: rgba(23, 50, 77, 0.24);
}

.dashboard-date-card {
    padding: 0.85rem;
    border: 1px solid rgba(23, 50, 77, 0.08);
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.76);
}

.dashboard-date-card__label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--dashboard-muted);
    font-size: 0.82rem;
    font-weight: 600;
}

.dashboard-date-card__fields {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 0.75rem;
}

.dashboard-date-card__separator {
    color: var(--dashboard-muted);
    font-weight: 700;
}

.dashboard-scroll-container {
    flex: 1 1 auto;
    min-height: 0;
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    overflow-y: auto;
    padding-right: 0.25rem;
}

.dashboard-overview-section {
    display: block;
    width: 100%;
}

.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem;
}

.dashboard-secondary-grid {
    display: grid;
    grid-template-columns: 1fr;
}

@media (max-width: 1200px) {
    .dashboard-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 991px) {
    .dashboard-page {
        padding: 1rem;
    }

    .dashboard-hero {
        position: static;
        grid-template-columns: 1fr;
        padding: 1.2rem;
    }
}

@media (max-width: 767px) {
    .dashboard-page {
        padding: 0.55rem;
    }

    .dashboard-hero {
        padding: 0.65rem;
        border-radius: 12px;
        gap: 0.55rem;
        box-shadow: 0 10px 28px rgba(31, 59, 87, 0.07);
    }

    .dashboard-hero__eyebrow {
        margin-bottom: 0.2rem;
        font-size: 0.62rem;
    }

    .dashboard-hero__title {
        margin-bottom: 0;
        font-size: 1rem;
        line-height: 1.2;
    }

    .dashboard-hero__text {
        display: none;
    }

    .dashboard-hero__controls {
        gap: 0.45rem;
    }

    .dashboard-range-buttons {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 0.35rem;
    }

    .dashboard-range-button {
        min-height: 32px;
        padding: 0.4rem 0.25rem;
        border-radius: 8px;
        font-size: 0.72rem;
        line-height: 1.1;
        text-align: center;
    }

    .dashboard-date-card {
        padding: 0.5rem;
        border-radius: 10px;
    }

    .dashboard-date-card__label {
        margin-bottom: 0.35rem;
        font-size: 0.68rem;
        text-transform: uppercase;
    }

    .dashboard-grid {
        grid-template-columns: 1fr;
    }

    .dashboard-date-card__fields {
        grid-template-columns: 1fr 1fr;
        gap: 0.45rem;
    }

    .dashboard-date-card__fields .form-control {
        min-height: 34px;
        padding: 0.35rem 0.45rem;
        font-size: 0.78rem;
    }

    .dashboard-date-card__separator {
        display: none;
    }

    .dashboard-scroll-container {
        gap: 0.75rem;
        margin-top: 0.75rem;
        padding-right: 0;
    }
}

@media (max-width: 380px) {
    .dashboard-range-buttons {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
